"use client";

import { Clock, Loader, Mail, MapPin, Phone } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Controller } from "react-hook-form";
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 rounded-lg border border-primary/30 bg-primary/10">
        <h3 className="font-default text-2xl text-foreground mb-3" style={{
        fontWeight: 450
      }}>
          Message received.
        </h3>
        <p className="text-base text-muted-foreground leading-relaxed mb-6">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="rounded-lg px-6 h-11 border-foreground/80 text-foreground hover:bg-secondary">
          Send another message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <FormField control={control} name="name" rules={{
      required: "Please share your name",
      minLength: {
        value: 2,
        message: "Name should be at least 2 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground text-sm font-semibold">
              Your Name
            </FormLabel>
            <FormControl>
              <Input placeholder="Jane Doe" className="bg-background border-b-2 border-input h-12 px-0 text-base focus-visible:ring-0 focus-visible:border-primary" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Please share your email",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground text-sm font-semibold">
              Email Address
            </FormLabel>
            <FormControl>
              <Input type="email" placeholder="jane@example.com" className="bg-background border-b-2 border-input h-12 px-0 text-base focus-visible:ring-0 focus-visible:border-primary" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="phone" rules={{
      pattern: {
        value: /^[+]?[\d\s\-().]{7,}$/,
        message: "Please enter a valid phone number"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground text-sm font-semibold">
              {"Phone Number "}
              <span className="text-muted-foreground font-normal">(optional)</span>
            </FormLabel>
            <FormControl>
              <Input type="tel" placeholder="(210)338-0049" className="bg-background border-b-2 border-input h-12 px-0 text-base focus-visible:ring-0 focus-visible:border-primary" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormItem>
        <FormLabel className="text-foreground text-sm font-semibold">
          Reason for Contact
        </FormLabel>
        <Controller control={control} name="subject" render={({
        field
      }) => <Select onValueChange={field.onChange} value={field.value || ""}>
              <SelectTrigger className="bg-background border-b-2 border-input h-12 px-0 text-base w-full focus-visible:ring-0 focus-visible:border-primary">
                <SelectValue placeholder="Select a topic" />
              </SelectTrigger>
              <SelectContent className="rounded-lg">
                <SelectItem value="Botox Consultation">Botox Consultation</SelectItem>
                <SelectItem value="Facial Balancing">Facial Balancing</SelectItem>
                <SelectItem value="Preventative Filler">Preventative Filler</SelectItem>
                <SelectItem value="General Inquiry">General Inquiry</SelectItem>
              </SelectContent>
            </Select>} />
        <FormMessage />
      </FormItem>

      <FormField control={control} name="message" rules={{
      required: "Please share your message",
      minLength: {
        value: 10,
        message: "Please provide a bit more detail"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground text-sm font-semibold">
              Message
            </FormLabel>
            <FormControl>
              <Textarea rows={5} placeholder="How can we help you achieve your aesthetic goals?" className="bg-background border-b-2 border-input px-0 py-3 text-base min-h-[140px] resize-y focus-visible:ring-0 focus-visible:border-primary" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 rounded-lg border border-destructive/30 bg-destructive/10">
          <p className="text-sm text-destructive">{submitError}</p>
        </div>}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2">
        <p className="text-sm text-muted-foreground">
          We respond within one business day.
        </p>
        <Button type="submit" disabled={isSubmitting} className="rounded-lg h-12 px-8 bg-primary text-primary-foreground hover:bg-primary/90 font-medium text-base shadow-md w-full sm:w-auto">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Sending...
            </> : "Send Message"}
        </Button>
      </div>
    </Form>;
}
export default function Contact() {
  const contactBlocks = [{
    icon: Phone,
    title: "(210)338-0049",
    detail: "Mon–Fri: 9am – 5pm",
    href: "tel:+12103380499"
  }, {
    icon: Mail,
    title: "hello@idealimagemed.com",
    detail: "Concierge support available",
    href: "mailto:hello@idealimagemed.com"
  }, {
    icon: MapPin,
    title: "Ideal Image Aesthetics and Wellness",
    detail: "26610 U.S. Hwy 281 N #109, San Antonio, TX 78260",
    href: "#"
  }];
  return <section data-section-id="10006" id="lets-connect" className="bg-background text-foreground py-8 md:py-20">
      <div className="max-w-[1240px] mx-auto px-6 md:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8">
          <div className="lg:col-span-5">
            <span className="inline-block text-xs uppercase mb-6 font-bold" style={{
            letterSpacing: "0.2em",
            color: "var(--primary)"
          }}>
              Concierge Care
            </span>

            <h1 className="font-default text-foreground mb-8 text-2xl sm:text-3xl md:text-4xl" style={{
            fontSize: "clamp(2.75rem, 6vw, 5rem)",
            fontWeight: 400,
            letterSpacing: "-0.02em",
            lineHeight: 1.04
          }}>
              {"Let's refine your "}
              <span className="relative inline-block italic" style={{
              fontWeight: 400
            }}>
                natural beauty
                <svg className="absolute left-0 w-full" style={{
                bottom: "-0.15em",
                height: "0.18em"
              }} viewBox="0 0 120 8" preserveAspectRatio="none" aria-hidden="true">
                  <path d="M2,5 Q30,1 60,4 T118,3" fill="none" stroke="var(--primary)" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </span>
              .
            </h1>

            <p className="text-muted-foreground mb-10 max-w-md" style={{
            fontSize: "clamp(1.125rem, 1.4vw, 1.3125rem)",
            lineHeight: 1.65,
            letterSpacing: "-0.005em"
          }}>
              {"Experience bespoke facial balancing and preventative care in a private, luxury setting."}
            </p>

            <div className="space-y-6">
              {contactBlocks.map((block, i) => {
              const Icon = block.icon;
              const content = <div className="flex items-start gap-4 group">
                    <div className="flex-shrink-0 mt-1 size-10 rounded-lg bg-primary/5 flex items-center justify-center">
                      <Icon className="size-5" strokeWidth={1.5} style={{
                    color: "var(--primary)"
                  }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-foreground font-semibold text-base mb-1">
                        {block.title}
                      </p>
                      <p className="text-sm text-muted-foreground whitespace-pre-line leading-relaxed">
                        {block.detail}
                      </p>
                    </div>
                  </div>;
              return <div data-index={i} key={i}>
                    {block.href !== "#" ? <Link to={block.href} className="block">
                        {content}
                      </Link> : content}
                  </div>;
            })}
            </div>
          </div>

          <div className="lg:col-span-7">
            <FormProvider formKey="connect_form_contact" formId="connect_form_contact" sectionName="lets-connect" defaultValues={{
            name: "",
            email: "",
            phone: "",
            subject: "",
            message: ""
          }} validationRules={{
            name: {
              required: true,
              minLength: 2
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              pattern: "phone"
            },
            message: {
              required: true,
              minLength: 10
            }
          }} submitText="Send Message" successMessage="Thank you for reaching out. We will be in touch shortly to schedule your consultation." errorMessage="Something went wrong. Please try again or call us directly." className="bg-card rounded-lg border border-border p-8 md:p-10 shadow-md">
              <h2 className="font-default text-foreground mb-2" style={{
              fontSize: "clamp(1.5rem, 2.2vw, 1.875rem)",
              fontWeight: 500,
              letterSpacing: "-0.01em",
              lineHeight: 1.2
            }}>
                Request a Consultation
              </h2>
              <p className="text-sm text-muted-foreground mb-8">
                Begin your journey to refined, effortless results.
              </p>
              <ContactForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}